"use client"

import { useRef, useState, useCallback, useEffect } from "react"
import { VideoOff, RotateCcw, Loader2 } from "lucide-react"

type StreamStatus = "connecting" | "live" | "error"

interface CameraStreamProps {
  id: string
  label: string
}

const CONNECT_TIMEOUT_MS = 15_000 // show error if no first frame in 15 s

export function CameraStream({ id, label }: CameraStreamProps) {
  const imgRef    = useRef<HTMLImageElement>(null)
  const timerRef  = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [status, setStatus]     = useState<StreamStatus>("connecting")
  const [errMsg,  setErrMsg]    = useState<string>("")

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  const startStream = useCallback((cacheBust = false) => {
    clearTimer()
    setStatus("connecting")
    setErrMsg("")

    // Timeout: if no frame arrives within CONNECT_TIMEOUT_MS → show error
    timerRef.current = setTimeout(() => {
      setStatus("error")
      setErrMsg("No frames received — check RTSP URL or ffmpeg install")
    }, CONNECT_TIMEOUT_MS)

    if (imgRef.current) {
      imgRef.current.src = cacheBust
        ? `/api/stream/${id}?t=${Date.now()}`
        : `/api/stream/${id}`
    }
  }, [id])

  // Start on mount; clear src on unmount → browser aborts request → ffmpeg killed
  useEffect(() => {
    startStream()
    return () => {
      clearTimer()
      if (imgRef.current) imgRef.current.src = ""
    }
  }, [startStream])

  const handleLoad = useCallback(() => {
    clearTimer()
    setStatus("live")
  }, [])

  const handleError = useCallback(async () => {
    clearTimer()

    // Try to get the error message from the API (e.g. "ffmpeg not installed")
    try {
      const res = await fetch(`/api/stream/${id}`, { method: "GET", signal: AbortSignal.timeout(3000) })
      if (!res.ok) {
        const text = await res.text()
        setErrMsg(text || `HTTP ${res.status}`)
      }
    } catch { /* network error */ }

    setStatus("error")
  }, [id])

  const dot = {
    connecting: "bg-yellow-400 animate-pulse",
    live:       "bg-emerald-500 animate-pulse shadow-[0_0_6px_rgba(52,211,153,0.6)]",
    error:      "bg-red-500",
  }[status]

  const badge = {
    connecting: "CONNECTING",
    live:       "LIVE",
    error:      "OFFLINE",
  }[status]

  return (
    <div className="flex flex-col rounded-xl border border-border bg-card overflow-hidden ring-1 ring-foreground/5">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border/40 bg-muted/30 shrink-0">
        <span className={`size-2 rounded-full shrink-0 ${dot}`} />
        <span className="text-[11px] font-semibold tracking-wide text-foreground truncate">{label}</span>
        <span className="ml-auto text-[10px] font-mono text-muted-foreground shrink-0">{badge}</span>
      </div>

      {/* Video area — preserves native 16:9 video aspect ratio */}
      <div className="relative aspect-video bg-black">
        {/* Connecting overlay */}
        {status === "connecting" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10">
            <Loader2 className="size-7 text-muted-foreground/40 animate-spin" />
            <span className="text-xs text-muted-foreground">Connecting to stream…</span>
          </div>
        )}

        {/* Error overlay */}
        {status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10 px-4">
            <VideoOff className="size-8 text-muted-foreground/30" />
            <span className="text-xs text-muted-foreground text-center">
              {errMsg || "Stream unavailable"}
            </span>
            <button
              onClick={() => startStream(true)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground underline transition-colors"
            >
              <RotateCcw className="size-3" /> Retry
            </button>
          </div>
        )}

        {/*
          The <img> is always in the DOM so the ref is valid.
          MJPEG via multipart/x-mixed-replace is natively supported in Chrome/Firefox.
          onLoad fires when the first JPEG frame renders.
        */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          ref={imgRef}
          alt={label}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            status === "live" ? "opacity-100" : "opacity-0"
          }`}
          onLoad={handleLoad}
          onError={handleError}
        />
      </div>
    </div>
  )
}
